import { type Subscription, type Project } from '@owl-nest/api-client/latest'
import { Reward } from './reward'
import { isMembership } from './project'

export function getReward<PROJECT_EXTRA_FIELDS extends string = ''>(
  subscription: Subscription,
  project: Project<PROJECT_EXTRA_FIELDS>,
): Reward<PROJECT_EXTRA_FIELDS> | undefined {
  if (subscription.reward) {
    return Reward(subscription.reward, project)
  }
}

// April 2025
// When KKBB was integrated to Ulule, there were 3 ways to contribute
// to a Membership project on Ulule:
// - subscribe to the free membership (it means you don't pay anything)
// - subscribe to a paying membership (it means you pay a monthly subscription)
// - make a donation (single tip)
//
// On KKBB, there was one more possibility: make a monthly donation (without any subscription).
// In order to replicate this kind of contribution, we introduced `Membership monthly donations`:
// these are monthly donations + subscription to the free reward.
export function isMembershipRecurringDonation<PROJECT_EXTRA_FIELDS extends string = ''>(
  subscription: Subscription | undefined,
  project: Project<PROJECT_EXTRA_FIELDS>,
): boolean {
  if (!isMembership(project) || subscription === undefined) {
    return false
  }

  return Boolean(subscription.total > 0 && subscription.is_donation)
}

export function isMembershipPayingSubscription<PROJECT_EXTRA_FIELDS extends string = ''>(
  subscription: Subscription | undefined,
  project: Project<PROJECT_EXTRA_FIELDS>,
): boolean {
  if (!isMembership(project) || subscription === undefined) {
    return false
  }

  const reward = getReward(subscription, project)
  return Boolean(subscription.total > 0 && reward && !reward.is_free)
}

export function isMembershipFreeSubscription<PROJECT_EXTRA_FIELDS extends string = ''>(
  subscription: Subscription | undefined,
  project: Project<PROJECT_EXTRA_FIELDS>,
): boolean {
  if (!isMembership(project) || subscription === undefined) {
    return false
  }

  return Boolean(subscription.total === 0 && getReward(subscription, project)?.is_free)
}
